import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { trackEvent, trackFormationView } from '../lib/track';
import { FormationCard, getDomainTone } from '../components/FormationCard';
import { FormationGallery } from '../components/FormationGallery';
import { ImgPlaceholder } from '../components/ImgPlaceholder';
import { ReservationDialog } from '../components/ReservationDialog';
import { StateMessage } from '../components/StateMessage';
import { useFormation, useFormateursById, useFormations } from '../data/queries';
import { useSEO } from '../hooks/useSEO';
import type { FormateurCouleur, Tone } from '../types';

const FORMATEUR_TONE: Record<FormateurCouleur, Tone> = {
  vert: 'vert',
  rouge: 'rouge',
  or: 'creme',
};

type Tab = 'programme' | 'formateurs' | 'pratique';

export function FormationDetail() {
  const navigate = useNavigate();
  const { id = '' } = useParams<{ id: string }>();
  const [tab, setTab] = useState<Tab>('programme');
  const [reserveOpen, setReserveOpen] = useState(false);

  const formationQ = useFormation(id);
  const formationsQ = useFormations();
  const formateursLookup = useFormateursById();

  const f = formationQ.data;

  // Count one detail-page view per session (powers the admin "most-viewed" panel).
  useEffect(() => {
    if (f?.id) trackFormationView(f.id);
  }, [f?.id]);

  const schemaOrg = useMemo(() => {
    if (!f) return undefined;
    return {
      '@context': 'https://schema.org',
      '@type': 'Course',
      name: f.titre,
      description: f.accroche,
      provider: {
        '@type': 'Organization',
        name: 'Moroccan Training Center',
        url: 'https://moroccantrainingcenter.com',
      },
      courseCode: f.id,
      educationalLevel: f.niveau,
      teaches: f.domaine,
      ...(f.heroImage && { image: f.heroImage }),
    };
  }, [f]);

  useSEO({
    title: f?.titre ?? 'Formation',
    description: f?.accroche,
    ogImage: f?.heroImage,
    schemaOrg,
  });

  if (formationQ.isLoading) return <StateMessage variant="loading" />;
  if (formationQ.error) return <StateMessage variant="error" message={String(formationQ.error)} />;

  if (!f) {
    return (
      <div className="page-detail">
        <p>Formation introuvable.</p>
        <button type="button" onClick={() => navigate('/formations')}>
          Retour
        </button>
      </div>
    );
  }

  const formateurs = f.intervenants
    .map((fid) => formateursLookup.byId[fid])
    .filter((fo): fo is NonNullable<typeof fo> => Boolean(fo));
  const tone = getDomainTone(f.domaine);
  const related = (formationsQ.data ?? []).filter((x) => x.id !== f.id).slice(0, 3);
  // "Dates à confirmer" = no édition open OR soldout → offer pre-reservation.
  const hasConfirmedEdition = f.upcomingSessions.some(
    (s) => s.registrationStatus === 'open' || s.registrationStatus === 'soldout',
  );

  return (
    <div className="page-detail">
      <button type="button" className="mtc-back" onClick={() => navigate('/formations')}>
        ← Toutes les formations
      </button>

      <div className="det-hero">
        <div className="det-hero-main">
          <div className="det-hero-meta">
            <span className={`mtc-pill mtc-pill-${tone}`}>{f.domaine}</span>
            <span className="mtc-pill mtc-pill-neutral">{f.niveau}</span>
            {f.tags.map((t) => (
              <span key={t} className="mtc-pill mtc-pill-neutral det-tag-extra">
                {t}
              </span>
            ))}
          </div>
          <h1>{f.titre}</h1>
          {formateurs.length > 0 && (
            <div className="det-hero-bylines">
              <span className="det-hero-bylines-label">
                {formateurs.length > 1 ? 'Animée par' : 'Animée par'}
              </span>
              <div className="det-hero-bylines-row">
                {formateurs.map((fo) => (
                  <button
                    key={fo.id}
                    type="button"
                    className={`det-hero-byline det-hero-byline-${fo.couleur}`}
                    onClick={() => navigate(`/formateurs/${fo.id}`)}
                    title={`Voir le profil de ${fo.nom}`}
                  >
                    {fo.photo ? (
                      <img
                        src={fo.photo}
                        alt=""
                        className="det-hero-byline-photo"
                        loading="lazy"
                      />
                    ) : (
                      <span className="det-hero-byline-photo det-hero-byline-photo-fallback">
                        {fo.nom
                          .split(/\s+/)
                          .map((p) => p[0])
                          .filter(Boolean)
                          .slice(0, 2)
                          .join('')
                          .toUpperCase()}
                      </span>
                    )}
                    <span className="det-hero-byline-text">
                      <strong>{fo.nom}</strong>
                      <span>{fo.titre}</span>
                    </span>
                    <span className="det-hero-byline-arrow" aria-hidden="true">
                      →
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
          <p className="det-hero-acc">{f.accroche}</p>
        </div>
        <aside className="det-sidebar">
          <div className="det-side-card">
            <div className="det-side-row">
              <span>Durée</span>
              <strong>
                {f.duree} · {f.heures}h
              </strong>
            </div>
            <div className="det-side-row">
              <span>Niveau</span>
              <strong>{f.niveau}</strong>
            </div>
            <div className="det-side-row">
              <span>Modules</span>
              <strong>{f.programme.length}</strong>
            </div>
            {formateurs.length > 0 && (
              <div className="det-side-row det-side-row-stack">
                <span>Formateurs</span>
                <strong>
                  {formateurs.map((fo, i) => (
                    <span key={fo.id}>
                      {fo.nom}
                      {i < formateurs.length - 1 ? ', ' : ''}
                    </span>
                  ))}
                </strong>
              </div>
            )}
            {/* Pricing — show prix par module everywhere on the public site;
                the full total only appears in the inscription flow. */}
            <div className="det-side-row det-side-row-prix">
              <span>Tarif par module</span>
              <strong className="det-prix">
                {f.prixParModule.toLocaleString('fr-FR')}{' '}
                <span className="unit">{f.devise} HT</span>
              </strong>
            </div>

            <div
              style={{
                marginTop: 24,
                paddingTop: 16,
                borderTop: '1px solid rgba(15, 25, 22, 0.08)',
              }}
            >
              <div
                style={{
                  fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                  fontSize: 10,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: 'rgba(15, 25, 22, 0.55)',
                  marginBottom: 14,
                }}
              >
                {f.upcomingSessions.length} édition{f.upcomingSessions.length > 1 ? 's' : ''} à venir
              </div>
              {f.upcomingSessions.map((s, idx) => (
                <div
                  key={s.id}
                  style={{
                    padding: '14px 0',
                    borderTop: idx > 0 ? '1px solid rgba(15, 25, 22, 0.08)' : 'none',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 12,
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 500, fontSize: 15, lineHeight: 1.3 }}>
                      {s.nom}
                    </div>
                    <div
                      style={{
                        marginTop: 4,
                        fontSize: 12,
                        color:
                          s.placesRestantes <= 4
                            ? 'var(--mtc-accent, #C8102E)'
                            : 'rgba(15, 25, 22, 0.6)',
                      }}
                    >
                      {s.lieu} · {s.placesRestantes} place
                      {s.placesRestantes > 1 ? 's' : ''} restante
                      {s.placesRestantes > 1 ? 's' : ''}
                      {s.placesRestantes <= 4 && s.placesRestantes > 0 && ' · bientôt complet'}
                    </div>
                  </div>

                  {s.moduleDates.length > 0 && (
                    <div
                      style={{
                        background: 'rgba(11, 93, 59, 0.04)',
                        border: '0.5px solid var(--mtc-line, rgba(15,25,22,0.1))',
                        borderRadius: 10,
                        padding: '10px 12px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8,
                      }}
                    >
                      <div
                        style={{
                          fontFamily: 'var(--mtc-font-mono)',
                          fontSize: 10,
                          letterSpacing: '0.16em',
                          textTransform: 'uppercase',
                          color: 'var(--mtc-primary, #0B5D3B)',
                        }}
                      >
                        Calendrier des modules
                      </div>
                      {s.moduleDates.map((md, mIdx) => {
                        const mod = f.programme[mIdx];
                        return (
                          <div
                            key={mIdx}
                            style={{
                              display: 'flex',
                              alignItems: 'baseline',
                              justifyContent: 'space-between',
                              gap: 12,
                              fontSize: 12.5,
                              lineHeight: 1.35,
                            }}
                          >
                            <span
                              style={{
                                color: 'rgba(15,25,22,0.85)',
                                minWidth: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                              title={`Module ${mIdx + 1}${mod ? ` · ${mod.titre}` : ''}`}
                            >
                              <strong style={{ fontWeight: 500 }}>
                                Module {mIdx + 1}
                              </strong>
                              {mod && <span style={{ opacity: 0.6 }}> · {mod.titre}</span>}
                            </span>
                            <span
                              style={{
                                color: 'var(--mtc-primary, #0B5D3B)',
                                fontWeight: 500,
                                whiteSpace: 'nowrap',
                                fontSize: 12,
                              }}
                            >
                              {md.range}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {s.registrationStatus === 'custom' && s.customStatus ? (
                    <div
                      style={{
                        textAlign: 'center',
                        padding: '12px 14px',
                        background: 'rgba(15,25,22,0.05)',
                        borderRadius: 999,
                        fontSize: 13,
                        fontWeight: 500,
                        color: 'rgba(15,25,22,0.75)',
                      }}
                    >
                      {s.customStatus}
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="mtc-btn mtc-btn-primary mtc-btn-block"
                      onClick={() => {
                        trackEvent('sinscrire_click');
                        navigate(`/inscription?formation=${f.id}&session=${s.id}`);
                      }}
                      disabled={
                        s.registrationStatus === 'soldout' ||
                        !s.registrationOpen ||
                        s.placesRestantes === 0
                      }
                    >
                      {s.registrationStatus === 'soldout' || s.placesRestantes === 0
                        ? 'Complète'
                        : "S'inscrire"}
                    </button>
                  )}
                </div>
              ))}
              {!hasConfirmedEdition && (
                <div style={{ marginTop: f.upcomingSessions.length > 0 ? 12 : 0 }}>
                  <p
                    style={{
                      fontSize: 13,
                      color: 'rgba(15, 25, 22, 0.6)',
                      lineHeight: 1.5,
                      marginBottom: 12,
                    }}
                  >
                    Les prochaines dates de cette formation seront bientôt annoncées.
                  </p>
                  <button
                    type="button"
                    className="mtc-btn mtc-btn-primary mtc-btn-block"
                    onClick={() => {
                      trackEvent('reserver_click');
                      setReserveOpen(true);
                    }}
                  >
                    Réserver votre place dès maintenant
                  </button>
                  <p
                    style={{
                      fontSize: 11,
                      color: 'rgba(15, 25, 22, 0.5)',
                      textAlign: 'center',
                      marginTop: 8,
                    }}
                  >
                    Sans engagement · vous serez contacté·e en priorité.
                  </p>
                </div>
              )}
            </div>

            {f.programmePdf && (
              <a
                href={f.programmePdf}
                target="_blank"
                rel="noreferrer"
                className="mtc-btn mtc-btn-ghost mtc-btn-block"
                style={{ marginTop: 16 }}
                onClick={() => trackEvent('affiche_download')}
              >
                Télécharger l'affiche (PDF) ↓
              </a>
            )}
          </div>
        </aside>
        <p className="det-hero-desc">{f.description}</p>
      </div>

      <div className="det-tabs">
        {(
          [
            ['programme', 'Programme'],
            ['formateurs', 'Formateurs'],
            ['pratique', 'Infos pratiques'],
          ] as const
        ).map(([tabId, lbl]) => (
          <button
            key={tabId}
            type="button"
            className={`det-tab ${tab === tabId ? 'is-on' : ''}`}
            onClick={() => setTab(tabId)}
          >
            {lbl}
          </button>
        ))}
      </div>

      <div className="det-tab-content">
        {tab === 'programme' && (
          <div className="det-programme">
            {f.programme.map((m) => {
              const moduleFormateurs = m.formateurIds
                .map((fid) => formateursLookup.byId[fid])
                .filter((fo): fo is NonNullable<typeof fo> => Boolean(fo));
              return (
                <div key={m.module} className="det-jour">
                  <div className="det-jour-head">
                    <span className="det-jour-tag">{m.module}</span>
                    <h3>{m.titre}</h3>
                  </div>
                  {moduleFormateurs.length > 0 && (
                    <div className="det-jour-meta">
                      <span
                        className="det-jour-meta-item"
                        data-label={
                          moduleFormateurs.length > 1 ? 'Formateurs' : 'Formateur'
                        }
                      >
                        {moduleFormateurs.map((fo) => fo.nom).join(', ')}
                      </span>
                    </div>
                  )}
                  <ul className="det-jour-list">
                    {m.points.map((p) => (
                      <li key={p}>{p}</li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        )}
        {tab === 'formateurs' && (
          <div className="det-formateurs">
            {formateurs.map((fo) => (
              <div key={fo.id} className="det-formateur">
                <ImgPlaceholder
                  label={fo.nom.split(' ').slice(-1)[0] ?? fo.nom}
                  ratio="1/1"
                  tone={FORMATEUR_TONE[fo.couleur]}
                  className="det-formateur-img"
                  src={fo.photo}
                  alt={fo.nom}
                />
                <div>
                  <h3>{fo.nom}</h3>
                  <p className="det-formateur-titre">{fo.titre}</p>
                  <p>{fo.bio}</p>
                  <button
                    type="button"
                    className="mtc-btn mtc-btn-link"
                    onClick={() => navigate(`/formateurs/${fo.id}`)}
                  >
                    Voir le profil →
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        {tab === 'pratique' && (
          <div className="det-pratique">
            <div className="det-pratique-grid">
              <div>
                <h4>Lieu</h4>
                <p>
                  {f.lieu}, {f.ville}.
                </p>
                <p style={{ whiteSpace: 'pre-line' }}>
                  {f.practicalInfo.lieu ||
                    'Accueil 30 min avant le début. Café et viennoiseries offerts.'}
                </p>
              </div>
              <div>
                <h4>Inclus</h4>
                <p style={{ whiteSpace: 'pre-line' }}>
                  {f.practicalInfo.inclus ||
                    'Support pédagogique, certificat de formation, repas du midi, accès au replay 12 mois, groupe alumni privé.'}
                </p>
              </div>
              <div>
                <h4>Pré-requis</h4>
                <p style={{ whiteSpace: 'pre-line' }}>
                  {f.practicalInfo.prerequis ||
                    (f.niveau === 'Débutant' || f.niveau === 'Tous niveaux'
                      ? 'Aucun pré-requis spécifique.'
                      : `Pratique régulière en ${f.domaine.toLowerCase()} requise.`)}
                </p>
              </div>
              <div>
                <h4>Annulation</h4>
                <p style={{ whiteSpace: 'pre-line' }}>
                  {f.practicalInfo.annulation ||
                    "Remboursement intégral jusqu'à 30 jours avant l'édition. Avoir valable 12 mois au-delà."}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      <FormationGallery formationId={f.id} formationTitle={f.titre} />

      <div className="det-related">
        <h3>Vous pourriez aussi aimer</h3>
        <div className="cat-grid">
          {related.map((x) => (
            <FormationCard key={x.id} f={x} onClick={() => navigate(`/formations/${x.id}`)} />
          ))}
        </div>
      </div>

      {reserveOpen && (
        <ReservationDialog
          formationId={f.id}
          formationTitle={f.titre}
          onClose={() => setReserveOpen(false)}
        />
      )}
    </div>
  );
}
